import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import {
  Button,
  CustomSelect,
  DataTable,
  DateRangePicker,
  Label,
} from "../Components";
import { API_POST_TRADELIST } from "../utils/api/ApiConstant";
import { postAxios } from "../Services/commonService";
import { toISTString } from "../helper/common";

const TradeListModal = ({ rowData, handleShowHide }) => {
  console.log("TradeListModal", rowData);
  const [payloadData, setPayloadData] = useState({
    server: rowData?.ServerName,
    fromDateTime: new Date(new Date().getTime() - 1 * 24 * 60 * 60 * 1000),
    toDateTime: new Date(),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(200);
  const { values, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: {
      startDate: new Date(new Date().getTime() - 1 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
      server: { label: rowData?.ServerName, value: rowData?.ServerName },
    },
    onSubmit: async (value) => {
      const payload = {
        server: value.server?.value,
        fromDateTime: value.startDate,
        toDateTime: value.endDate,
      };
      setPayloadData(payload);
      await refetch();
    },
  });

  const {
    isLoading,
    data: tradeListModal,
    refetch,
  } = useQuery({
    queryKey: ["tradeListModal", payloadData],
    queryFn: async () => {
      const param = {
        server: payloadData.server,
        fromDateTime: toISTString(payloadData.fromDateTime),
        toDateTime: toISTString(payloadData.toDateTime),
        ScriptToken: rowData?.ScriptToken,
        OrderStatus: "S",
      };
      const res = await postAxios({
        url: API_POST_TRADELIST,
        payload: param,
      });
      return res;
    },
  });

  const { data: serverList } = useQuery({
    queryKey: ["serverList"],
    queryFn: async () => {
      const res = await getAxios({ url: API_GET_SERVERS, params: {} });
      return res || []; // Assuming API response structure
    },
  });

  const serverOptions = serverList
    ?.sort((a, b) => a.localeCompare(b))
    .map((server) => ({
      label: server,
      value: server,
    }));

  const columns = [
    {
      accessorKey: "F",
      header: "ExecutionTime",
      cell: ({ getValue }) => {
        const rawDate = getValue();
        const date = new Date(rawDate);

        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata", // force IST
        };

        return date.toLocaleString("en-IN", options);
      },
    },
    { accessorKey: "A", header: "UserName" },
    { accessorKey: "B", header: "ParentUser" },
    { accessorKey: "AE", header: "Server" },
    { accessorKey: "C", header: "Exchange" },
    // { accessorKey: "D", header: "ScriptToken" },
    { accessorKey: "E", header: "Script" },
    { accessorKey: "AB", header: "Exp.Date" },
    // { accessorKey: "AC", header: "ExpiryDate" },
    // { accessorKey: "G", header: "SymbolWithExpiry" },
    {
      accessorKey: "H",
      header: "Type",
      cell: ({ getValue }) => {
        const value = getValue();
        const isBuyType =
          value === "Buy" || value === "Buy Limit" || value === "Buy StopLoss";

        return (
          <div
            style={{
              cursor: "pointer",
              color: isBuyType ? "green" : "red",
            }}
          >
            {value}
          </div>
        );
      },
    },
    // { accessorKey: "I", header: "BuySell" },
    {
      accessorKey: "J",
      header: "Qty",
      cell: ({ row, getValue }) => {
        const value = getValue();
        const isBuyType =
          row.original.H === "Buy" ||
          row.original.H === "Buy Limit" ||
          row.original.H === "Buy StopLoss";

        return (
          <div
            style={{
              cursor: "pointer",
              color: isBuyType ? "green" : "red",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      accessorKey: "K",
      header: "Price",
      cell: ({ row, getValue }) => {
        const value = getValue();
        const isBuyType =
          row.original.H === "Buy" ||
          row.original.H === "Buy Limit" ||
          row.original.H === "Buy StopLoss";
        return (
          <div
            style={{
              cursor: "pointer",
              color: isBuyType ? "green" : "red",
            }}
          >
            {value}
          </div>
        );
      },
    },
    { accessorKey: "AD", header: "P&L" },
    { accessorKey: "L", header: "Brk" },
    { accessorKey: "M", header: "Ref.Price" },
    {
      accessorKey: "N",
      header: "OrderTime",
      cell: ({ getValue }) => {
        const rawDate = getValue();
        const date = new Date(rawDate);

        const options = {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
          timeZone: "Asia/Kolkata", // force IST
        };

        return date.toLocaleString("en-IN", options);
      },
    },
    // { accessorKey: "O", header: "OrderDate" },
    // { accessorKey: "P", header: "OrderStatus" },
    { accessorKey: "Q", header: "Status" },
    { accessorKey: "X", header: "Method" },
    { accessorKey: "R", header: "IPAddress" },
    // { accessorKey: "S", header: "Description" },
    // { accessorKey: "T", header: "LotSize" },
    // { accessorKey: "U", header: "TickSize" },
    // { accessorKey: "V", header: "OrderType" },
    // { accessorKey: "W", header: "UserHLTrade" },
    // { accessorKey: "Y", header: "DeviceId" },
    // { accessorKey: "Z", header: "OrderTypeText" },
    // { accessorKey: "AA", header: "OrderNo" },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRecordsperPage = (value) => {
    setRecordsPerPage(value);
  };

  const fetchData = () => {
    if (!tradeListModal) return { data: [], pages: 0 };

    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;

    return {
      data: tradeListModal.slice(startIndex, endIndex),
      pages: Math.ceil(tradeListModal.length / recordsPerPage),
    };
  };

  return (
    <>
      <div>
        <form className="date-input-main" onSubmit={handleSubmit}>
          <div className="row m-0 p-0 align-items-end">
            <div className="col-lg-2 col-2 m-0 p-1">
              <div className="date-input-box">
                <Label htmlFor="" className="lead_text">
                  Servers
                </Label>
                <CustomSelect
                  className="dropdown-control "
                  options={serverOptions}
                  onChange={(selectedOption) => {
                    setFieldValue("server", selectedOption);
                  }}
                  value={values.server}
                  name="server"
                  id="server"
                  isClearable={false}
                />
              </div>
            </div>
            <DateRangePicker
              values={values}
              setFieldValue={setFieldValue}
              className="date-input-box"
              customClass="col-lg-2 col-12 m-0 p-1 "
            />
            <div className="col-lg-1 col-6 m-0 p-1">
              <Button
                type="submit"
                className="ban-man-box history-btn"
                title="View"
              >
                View
              </Button>
            </div>
            <div className="col-lg-1 col-6 m-0 p-1">
              <Button
                type="button"
                className="ban-man-box clear-btn"
                title="Clear"
                onClick={() => {
                  resetForm();
                  setPayloadData({
                    server: "All",
                    fromDateTime: new Date(
                      new Date().getTime() - 7 * 24 * 60 * 60 * 1000
                    ),
                    toDateTime: new Date(),
                  });
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        </form>
      </div>
      <DataTable
        columns={columns}
        // fetchData={fetchData}
        data={fetchData().data}
        pages={fetchData().pages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onRecordsPerPageChange={handleRecordsperPage}
        recordsPerPage={recordsPerPage}
        isLoading={isLoading}
        isSearchable={false}
        isPagination={true}
        isPageSize={true}
        isIndexing={true}
      />
    </>
  );
};

export default TradeListModal;
